/**
 * ThemeContext.jsx — src/context/ThemeContext.jsx
 *
 * 1. Injects CSS variables on :root → works with SCSS modules automatically
 * 2. Provides useTheme() hook for inline JS styles
 * 3. ThemeToggle — drop into any header
 */

import React, { createContext, useContext, useState, useEffect } from "react";

export const THEMES = {
  dark: {
    bg:        "#06101F",
    card:      "#0D1B2E",
    card2:     "#0A1628",
    border:    "rgba(0,200,255,0.09)",
    cyan:      "#00C8FF",
    purple:    "#7C5CFF",
    green:     "#00E5A3",
    amber:     "#FFB800",
    red:       "#FF4D6D",
    text:      "#E8F0FE",
    muted:     "#7A93B4",
    dim:       "#3D5473",
    inp_bg:    "rgba(255,255,255,0.04)",
    opt_bg:    "#0D1B2E",
    toggle_bg: "rgba(255,255,255,0.08)",
    scheme:    "dark",
    // sidebar specific
    sidebar_bg:      "#0A1628",
    sidebar_border:  "rgba(0,200,255,0.09)",
    sidebar_hover:   "rgba(0,200,255,0.07)",
    sidebar_active:  "rgba(0,200,255,0.12)",
    header_bg:       "#0D1B2E",
    header_border:   "rgba(0,200,255,0.09)",
    nav_text:        "#7A93B4",
    nav_text_active: "#00C8FF",
    submenu_bg:      "rgba(0,200,255,0.04)",
    dropdown_bg:     "#0D1B2E",
    shadow:          "0 8px 32px rgba(0,0,0,0.4)",
    // extra accent + pre-baked alpha tokens (used by hrms.module.scss, which
    // can't run sass color functions on var() values — so alpha is pre-mixed here)
    blue:            "#3B82F6",
    border_strong:   "rgba(0,200,255,0.2)",
    card_hover:      "#112240",
    cyan_soft:       "rgba(0,200,255,0.12)",
    purple_soft:     "rgba(124,92,255,0.12)",
    green_soft:      "rgba(0,229,163,0.12)",
    amber_soft:      "rgba(255,184,0,0.12)",
    red_soft:        "rgba(255,77,109,0.12)",
    blue_soft:       "rgba(59,130,246,0.12)",
    green_soft2:     "rgba(0,229,163,0.15)",
    red_soft2:       "rgba(255,77,109,0.15)",
    amber_soft2:     "rgba(255,184,0,0.15)",
    amber_border:    "rgba(255,184,0,0.3)",
    cyan_border:     "rgba(0,200,255,0.3)",
    purple_border:   "rgba(124,92,255,0.3)",
    green_border:    "rgba(0,229,163,0.3)",
    red_border:      "rgba(255,77,109,0.3)",
    cyan_faint:      "rgba(0,200,255,0.06)",
    surface_soft:    "rgba(255,255,255,0.05)",
    surface_med:     "rgba(255,255,255,0.07)",
    surface_faint:   "rgba(255,255,255,0.02)",
    hover_border:    "rgba(255,255,255,0.1)",
    shadow_soft:     "0 8px 32px rgba(0,0,0,0.2)",
  },
  light: {
    bg:        "#FAF8F3",
    card:      "#FFFDF7",
    card2:     "#F6F1E8",
    border:    "rgba(0,0,0,0.09)",
    cyan:      "#e35222ff",
    purple:    "#6340C8",
    green:     "#00965A",
    amber:     "#C47D00",
    red:       "#D93025",
    text:      "#1A2332",
    muted:     "#5A6B82",
    dim:       "#9BA8B8",
    inp_bg:    "#F3EEE3",
    opt_bg:    "#FFFDF7",
    toggle_bg: "rgba(0,0,0,0.06)",
    scheme:    "light",
    // sidebar specific
    sidebar_bg:      "#FAF8F3",
    sidebar_border:  "rgba(0,0,0,0.09)",
    sidebar_hover:   "#F6F1E8",
    sidebar_active:  "#F1EADD",
    header_bg:       "#FAF8F3",
    header_border:   "rgba(0,0,0,0.09)",
    nav_text:        "#5A6B82",
    nav_text_active: "#1A2332",
    submenu_bg:      "#F8F4EC",
    dropdown_bg:     "#FFFDF7",
    shadow:          "0 8px 32px rgba(0,0,0,0.12)",
    // extra accent + pre-baked alpha tokens (used by hrms.module.scss, which
    // can't run sass color functions on var() values — so alpha is pre-mixed here)
    blue:            "#2563EB",
    border_strong:   "rgba(0,120,204,0.2)",
    card_hover:      "#F1EADD",
    cyan_soft:       "rgba(0,120,204,0.12)",
    purple_soft:     "rgba(99,64,200,0.12)",
    green_soft:      "rgba(0,150,90,0.12)",
    amber_soft:      "rgba(196,125,0,0.12)",
    red_soft:        "rgba(217,48,37,0.12)",
    blue_soft:       "rgba(37,99,235,0.12)",
    green_soft2:     "rgba(0,150,90,0.15)",
    red_soft2:       "rgba(217,48,37,0.15)",
    amber_soft2:     "rgba(196,125,0,0.15)",
    amber_border:    "rgba(196,125,0,0.3)",
    cyan_border:     "rgba(0,120,204,0.3)",
    purple_border:   "rgba(99,64,200,0.3)",
    green_border:    "rgba(0,150,90,0.3)",
    red_border:      "rgba(217,48,37,0.3)",
    cyan_faint:      "rgba(0,120,204,0.06)",
    surface_soft:    "rgba(0,0,0,0.04)",
    surface_med:     "rgba(0,0,0,0.05)",
    surface_faint:   "rgba(0,0,0,0.02)",
    hover_border:    "rgba(0,0,0,0.1)",
    shadow_soft:     "0 8px 32px rgba(0,0,0,0.08)",
  },
};

const ThemeContext = createContext({
  theme:       "dark",
  T:           THEMES.dark,
  toggleTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(
    () => localStorage.getItem("hrms_theme") || "dark"
  );
  const T = THEMES[theme] || THEMES.dark;

  useEffect(() => {
    const root = document.documentElement;

    // ── Inject every token as a CSS variable on :root ──────────────
    // SCSS can use var(--hrms-bg), var(--hrms-card), etc.
    Object.entries(T).forEach(([key, val]) => {
      root.style.setProperty(`--hrms-${key.replace(/_/g, "-")}`, val);
    });

    // Body background
    document.body.style.backgroundColor = T.bg;
    document.body.style.color           = T.text;
    document.body.style.transition      = "background-color 0.25s, color 0.25s";
  }, [T]);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("hrms_theme", next);
  };

  return (
    <ThemeContext.Provider value={{ theme, T, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// ── Drop-in toggle button ──────────────────────────────────────────
export function ThemeToggle() {
  const { theme, T, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
      style={{
        padding:     "6px 14px",
        borderRadius: 8,
        cursor:       "pointer",
        border:      `1px solid ${T.border}`,
        background:   T.toggle_bg,
        color:        T.muted,
        fontFamily:  "Outfit, sans-serif",
        fontSize:     12,
        fontWeight:   700,
        display:     "flex",
        alignItems:  "center",
        gap:          6,
        transition:  "all 0.2s",
        whiteSpace:  "nowrap",
      }}
    >
      {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
    </button>
  );
}